import React from 'react';
import { PreviewMode } from '../components/PreviewMode';
import type { CrudPageProps } from './types';

function LoadingSpinner({ text }: { text?: string }) {
	return (
		<div style={{
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			gap: '12px',
			padding: '40px'
		}}>
			<div style={{
				width: '32px',
				height: '32px',
				border: '3px solid rgba(255,255,255,0.1)',
				borderTop: '3px solid rgba(59,130,246,0.95)',
				borderRadius: '50%',
				animation: 'spin 0.8s linear infinite'
			}} />
			{text && (
				<span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)' }}>
					{text}
				</span>
			)}
			<style>{`
				@keyframes spin {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
			`}</style>
		</div>
	);
}

export function CrudPage({ sdk, pageId, id }: CrudPageProps) {
	const pid = id || pageId;
	
	if (!pid) {
		throw new Error('CrudPage requires either "pageId" or "id" prop');
	}
	
	const { data: page, isLoading, error } = sdk.api.useQuery(
		['crud-page', pid],
		async () => {
			const res = await sdk.api.fetch(`/crud/pages/${pid}`);
			return res.json();
		}
	);
	
	if (isLoading) {
		return <LoadingSpinner text="Loading page..." />;
	}
	
	if (error) {
		return (
			<div style={{ 
				padding: '32px', 
				textAlign: 'center',
				background: 'var(--error-bg)',
				border: '1px solid var(--error-border)',
				borderRadius: '8px',
				color: 'var(--error-color)'
			}}>
				<div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
				<div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
					Error Loading Page
				</div>
				<div style={{ fontSize: '14px', opacity: 0.8 }}>
					{error instanceof Error ? error.message : 'Failed to load page'}
				</div>
			</div>
		);
	}
	
	if (!page) {
		return (
			<div style={{ padding: '32px', textAlign: 'center', color: 'var(--main-text-secondary)' }}>
				Page not found
			</div>
		);
	}
	
	return (
		<PreviewMode 
			page={page} 
			autoLoadData={true}
			hideExitButton={true}
		/>
	);
}

